import { DeleteOutlined, EditOutlined, FileOutlined, FileSyncOutlined, MailOutlined, PlusOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Image,
  Input,
  InputNumber,
  Layout,
  Menu,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import { useApplicationContext } from "components/Application";
import { CenterResult } from "components/CenterResult";
import { CenterSpinner } from "components/CenterSpinner";
import { find } from "lodash";
import moment from "moment";
import React, { Fragment, ReactElement, ReactNode, useState } from "react";
import { NavLink, useParams } from "react-router-dom";

const { Title, Text } = Typography;

const orderDocumentQuery = gql`
  query($id: ID!) {
    orderDocument(id: $id) {
      id
      code
      status
      updatedAt
      seasonKind
      document
      notes
      user {
        id
        companyName
        address
        city
        zip
        country
        vatNumber
        priceList {
          id
          code
          prices {
            id
            itemId
          }
        }
      }
      paymentMethod {
        nameEn
      }
      rows {
        id
        item {
          id
          code
          cover
          color {
            id
            code
            nameEn
            patch
          }
          stocks {
            quantity
            size {
              id
            }
          }
          brand {
            id
          }
          season {
            id
          }
        }
        referenceItem {
          id
          code
          nameEn
          shortDescriptionEn
          sizeGroup {
            id
            sizes {
              id
              code
              value
            }
          }
        }
        sizeQuantities {
          id
          quantity
          size {
            id
            code
          }
        }
        quantity
        price
        total
      }
      quantity
      total
      taxes
      grandTotal
    }
  }
`;

const renderOrderDocumentMutation = gql`
  mutation($id: ID!) {
    renderOrderDocument(id: $id) {
      document {
        id
        document
      }
    }
  }
`;

const mailingOrderDocumentMutation = gql`
  mutation($id: ID!) {
    mailingOrderDocument(id: $id) {
      document {
        id
      }
    }
  }
`;

const usersQuery = gql`
  query {
    accountUsers(role: "user") {
      nodes {
        id
        code
        companyName
        priceList {
          id
        }
      }
    }
  }
`;
type UsersQueryType = (props: { children: (data: any) => ReactNode }) => ReactElement;

const UsersQuery: UsersQueryType = (props) => {
  const { children } = props;

  const { error, loading, data } = useQuery(usersQuery, {
    fetchPolicy: "cache-and-network",
  });

  if (loading) return <Spin />;
  if (error || !data) return <>NOT FOUND</>;

  return <Fragment>{children(data.accountUsers.nodes)}</Fragment>;
};

const articleItemsQuery = gql`
  query($brandId: String, $seasonId: String, $stocked: Boolean) {
    articleItems(brandId: $brandId, seasonId: $seasonId, stocked: $stocked, includeVariants: true) {
      nodes {
        id
        code
        nameEn
        cover
        sizeGroup {
          id
          sizes {
            id
            code
            value
          }
        }
        stocks {
          quantity
          size {
            id
          }
        }
        referenceItem {
          id
        }
      }
    }
  }
`;
type ItemsQueryType = (props: { children: (data: any) => ReactNode; variables: any }) => ReactElement;

const ItemsQuery: ItemsQueryType = (props) => {
  const { children, variables } = props;

  const { error, loading, data } = useQuery(articleItemsQuery, {
    variables,
    fetchPolicy: "cache-and-network",
  });

  if (loading) return <Spin />;
  if (error || !data) return <>NOT FOUND</>;

  return <Fragment>{children(data.articleItems.nodes)}</Fragment>;
};

const updateOrderDocumentMutation = gql`
  mutation($id: ID!, $input: UpdateOrderDocumentInput!) {
    updateOrderDocument(id: $id, input: $input) {
      document {
        id
        user {
          id
        }
      }
    }
  }
`;
const updateOrderRowMutation = gql`
  mutation($id: ID!, $input: UpdateOrderRowInput!) {
    updateOrderRow(id: $id, input: $input) {
      row {
        id
      }
    }
  }
`;
const deleteOrderRowMutation = gql`
  mutation($id: ID!) {
    deleteOrderRow(id: $id) {
      successful
    }
  }
`;

const createOrderRowMutation = gql`
  mutation($input: CreateOrderRowInput!) {
    createOrderRow(input: $input) {
      row {
        id
      }
    }
  }
`;

export const Show: React.FC<{}> = () => {
  const {
    session,
    siteEnvironment: { appEndpointStore },
  } = useApplicationContext();
  const { id } = useParams();
  const [newRowForm] = Form.useForm();

  const { error, loading: queryLoading, data, refetch } = useQuery(orderDocumentQuery, {
    variables: { id },
    fetchPolicy: "cache-and-network",
  });

  const [generatePdf] = useMutation(renderOrderDocumentMutation, {
    onError: (error) => {
      message.error("There was an error");
    },
    onCompleted: (data) => {
      message.success("PDF successfully generated");
    },
  });

  const [sendEmails] = useMutation(mailingOrderDocumentMutation, {
    onError: (error) => {
      message.error("There was an error");
    },
    onCompleted: (data) => {
      message.success("Emails successfully sent");
    },
  });

  const [editingCustomer, setEditingCustomer] = useState(false);

  const [userMutation, { loading: userLoading }] = useMutation(updateOrderDocumentMutation, {
    errorPolicy: "none",
    onError: (error) => {
      message.error("There was an error");
    },
    onCompleted: (data) => {
      setEditingCustomer(false);
      resetNewRow();
    },
  });

  const updateUser = (data: any) => {
    const { userId } = data;
    userMutation({ variables: { id, input: { userId } } });
  };

  const [editingRow, setEditingRow] = useState(null);

  const [rowMutation, { loading: rowLoading }] = useMutation(updateOrderRowMutation, {
    errorPolicy: "none",
    onError: (error) => {
      message.error("There was an error");
    },
    onCompleted: (data) => {
      setEditingRow(null);
      refetch();
    },
  });

  const updateRow = (id: any, input: any) => {
    rowMutation({ variables: { id, input } });
  };

  const [deleteRow] = useMutation(deleteOrderRowMutation, {
    onError: (error) => {
      message.error("There was an error");
    },
    onCompleted: (data) => {
      refetch();
      resetNewRow();
    },
  });

  const [addingRow, setAddingRow] = useState(false);

  const [newRowMutation, { loading: newRowLoading }] = useMutation(createOrderRowMutation, {
    errorPolicy: "none",
    onError: (error) => {
      message.error("There was an error");
    },
    onCompleted: (data) => {
      refetch();
      resetNewRow();
    },
  });

  const resetNewRow = () => {
    setAddingRow(false);
    newRowForm.resetFields();
    setNewItem({});
  };

  const addRow = (data: any) => {
    const referenceItemId = newItem.referenceItem?.id ?? newItem.id;
    const input = { ...data, documentId: id, referenceItemId };
    newRowMutation({ variables: { input } });
  };

  const [newItem, setNewItem] = useState<any>({});

  const itemChange = (id: any, option: any) => setNewItem(option.item);

  if (queryLoading) return <CenterSpinner size="large" />;
  if (error)
    return (
      <CenterResult
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
        extra={
          <NavLink to="../">
            <Button type="primary">Back to list</Button>
          </NavLink>
        }
      />
    );

  const resource = data.orderDocument;
  const stock = resource.seasonKind === "stock";

  const renderSizesFields = (sizes: any, stocks: any, quantites?: any) => {
    return sizes?.map((size: any, j: any) => {
      let currentQuantity = 0;
      quantites?.forEach((sizeQuantity: any) => {
        if (sizeQuantity.size.id === size.id) currentQuantity = sizeQuantity.quantity;
      });
      let availability = 0;
      if (stock)
        stocks?.forEach((stock: any) => {
          if (stock.size.id === size.id && stock.quantity > 0) {
            availability = stock.quantity;
          }
        });
      return (
        <Fragment key={j}>
          <Form.Item hidden name={["sizeQuantities", j, "sizeId"]} initialValue={size.id}>
            <Input />
          </Form.Item>
          <Form.Item
            name={["sizeQuantities", j, "quantity"]}
            initialValue={currentQuantity}
            style={{ margin: 0, marginBottom: "10px" }}
          >
            <InputNumber
              min={0}
              style={{ width: "60px" }}
              onFocus={(e) => e.target.select()}
              max={stock ? availability : 999}
              disabled={stock && availability === 0}
            />
          </Form.Item>
          <span style={{ margin: "5px", marginRight: "25px" }}>{size.code}</span>
        </Fragment>
      );
    });
  };

  const brandId = resource.rows[0]?.item.brand.id;
  const seasonId = resource.rows[0]?.item.season.id;

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border">
              <Title level={4} style={{ lineHeight: "auto" }}>
                Order
              </Title>
            </Menu.Item>
          </Menu>
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border">
              <NavLink to="../">
                <Button>Back</Button>
              </NavLink>
            </Menu.Item>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <Layout.Content className="content">
          <Card>
            <Row>
              <Col flex={1} span={20}>
                <Row gutter={[25, 25]}>
                  <Col span={8}>
                    <Title level={5}>Date</Title>
                    <Text>{moment(resource.updatedAt).format("DD-MM-YYYY")}</Text>
                    <Title level={5}>
                      <Space>
                        Customer
                        {(!editingCustomer && session.currentUser.role === "ADMIN") && (
                          <Tooltip title="Edit Customer" placement="right">
                            <Button onClick={() => setEditingCustomer(true)} icon={<EditOutlined />}></Button>
                          </Tooltip>
                        )}
                      </Space>
                    </Title>
                    {editingCustomer && (
                      <UsersQuery>
                        {(users) => (
                          <Form onFinish={updateUser}>
                            <Form.Item name="userId" initialValue={resource.user.id}>
                              <Select
                                showSearch
                                filterOption={(input: string, option: any) => {
                                  return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                                }}
                              >
                                {users
                                  .filter((user: any) => user.priceList.id === resource.user.priceList.id)
                                  .map((user: any) => (
                                    <Select.Option
                                      key={user.id}
                                      value={user.id}
                                      label={`${user.code} - ${user.companyName}`}
                                    >{`${user.code} - ${user.companyName}`}</Select.Option>
                                  ))}
                              </Select>
                            </Form.Item>
                            <Space>
                              <Button htmlType="button" onClick={() => setEditingCustomer(false)}>
                                Cancel
                              </Button>
                              <Button type="primary" htmlType="submit" loading={userLoading}>
                                Save
                              </Button>
                            </Space>
                          </Form>
                        )}
                      </UsersQuery>
                    )}
                    {!editingCustomer && (
                      <>
                        <Text>{resource.user.companyName}</Text>
                        <br />
                        <Text>{resource.user.address}</Text>
                        <br />
                        <Text>
                          {resource.user.city} {resource.user.zip} - {resource.user.country}
                        </Text>
                        <br />
                        <Text>Vat Number: {resource.user.vatNumber}</Text>
                      </>
                    )}
                  </Col>
                </Row>
                <Row gutter={[50, 25]}>
                  <Col>
                    <Title level={5}>Delivery window</Title>
                    <Text>{stock ? "10 Days from confirmation" : "---"}</Text>
                  </Col>
                  <Col>
                    <Title level={5}>Payment method</Title>
                    <Text>{resource.paymentMethod.nameEn}</Text>
                  </Col>
                </Row>
                {resource.notes && (
                  <Row>
                    <Col>
                      <Title level={5}>Notes</Title>
                      <Text>{resource.notes}</Text>
                    </Col>
                  </Row>
                )}
              </Col>
              <Col span={4}>
                <Row justify="end">
                  <Col>
                    <Space>
                      {resource.document && (
                        <Tooltip title="Download PDF" placement="bottom">
                          <a target="_blank" rel="noreferrer" href={`${appEndpointStore}${resource.document}`}>
                            <Button icon={<FileOutlined />}></Button>
                          </a>
                        </Tooltip>
                      )}
                      {session.currentUser.role === "ADMIN" && (
                        <Tooltip title="Generate PDF" placement="bottom">
                          <Button icon={<FileSyncOutlined />} onClick={() => generatePdf({ variables: { id } })}></Button>
                        </Tooltip>
                      )}
                      {session.currentUser.role === "ADMIN" && (
                        <Popconfirm
                          title="Are you sure to send emails again?"
                          icon={null}
                          onConfirm={() => sendEmails({ variables: { id } })}
                          >
                          <Tooltip title="Send Emails" placement="bottom">
                            <a target="_blank" rel="noreferrer" href={`${appEndpointStore}${resource.document}`}>
                              <Button icon={<MailOutlined />}></Button>
                            </a>
                          </Tooltip>
                        </Popconfirm>
                      )}
                    </Space>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Divider />
            {resource.rows?.map((row: any, i: any) => (
              <Row gutter={[25, 25]} key={i}>
                <Col span={4}>
                  <Image src={`${appEndpointStore}${row.item.cover}`} width={100} />
                </Col>
                <Col span={6}>
                  <b>{row.referenceItem.nameEn}</b>
                  <br />
                  {row.referenceItem.shortDescriptionEn}
                  <br />
                  <Image src={`${appEndpointStore}${row.item.color.patch}`} width={20} height={20} /> {row.item.color.code} -{" "}
                  {row.item.color.nameEn}
                </Col>
                <Col span={10}>
                  <Row gutter={[25, 25]}>
                    {editingRow === i && (
                      <Space>
                        <Form onFinish={(data) => updateRow(row.id, data)} layout="inline">
                          {renderSizesFields(row.referenceItem.sizeGroup?.sizes, row.item.stocks, row.sizeQuantities)}
                          <Space>
                            <Button htmlType="button" onClick={() => setEditingRow(null)}>
                              Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" loading={rowLoading}>
                              Save
                            </Button>
                          </Space>
                        </Form>
                      </Space>
                    )}
                    {editingRow !== i && (
                      <>
                        {row.sizeQuantities
                          ?.filter((sizeQuantity: any) => sizeQuantity.quantity > 0)
                          .map((sizeQuantity: any, j: any) => (
                            <Col key={j}>
                              {sizeQuantity.quantity} / {sizeQuantity.size.code}
                            </Col>
                          ))}
                      </>
                    )}
                  </Row>
                </Col>
                <Col span={3}>
                  Qty: {row.quantity}
                  <br />
                  Price: {row.price}
                  <br />
                  Total: {row.total}
                </Col>
                <Col span={1}>
                  <Space direction="vertical">
                    {(editingRow !== i && session.currentUser.role === "ADMIN") && (
                      <Tooltip title="Edit Quantities" placement="left">
                        <Button icon={<EditOutlined />} onClick={() => setEditingRow(i)}></Button>
                      </Tooltip>
                    )}
                    {editingRow === i &&  <div style={{ height: "32px" }} />}
                    {session.currentUser.role === "ADMIN" && (
                      <Popconfirm
                        title="Are you sure to delete this row?"
                        icon={null}
                        placement="leftTop"
                        onConfirm={() => deleteRow({ variables: { id: row.id } })}
                      >
                        <Button danger icon={<DeleteOutlined />}></Button>
                      </Popconfirm>
                    )}
                  </Space>
                </Col>
              </Row>
            ))}
            <Form form={newRowForm} onFinish={addRow}>
              <Row gutter={[25, 25]}>
                <Col span={4}>{newItem.cover && <Image src={`${appEndpointStore}${newItem.cover}`} width={100} />}</Col>
                <Col span={6}>
                  {(!addingRow && session.currentUser.role === "ADMIN") && (
                    <Button onClick={() => setAddingRow(true)}>
                      <PlusOutlined />
                      Add Item
                    </Button>
                  )}
                  {addingRow && (
                    <ItemsQuery variables={{ brandId, seasonId, stocked: stock }}>
                      {(items) => (
                        <Form.Item name="itemId">
                          <Select
                            showSearch
                            onChange={itemChange}
                            filterOption={(input: string, option: any) => {
                              return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                            }}
                          >
                            {items
                              .filter((item: any) =>
                                !resource.rows?.map((row: any) => row.item.id).includes(item.id) &&
                                find(resource.user.priceList?.prices, { itemId: item.id })
                                  ? true
                                  : false,
                              )
                              .map((item: any) => (
                                <Select.Option key={item.id} value={item.id} label={item.code} item={item}>
                                  {item.code}
                                </Select.Option>
                              ))}
                          </Select>
                        </Form.Item>
                      )}
                    </ItemsQuery>
                  )}
                </Col>
                <Col span={10}>
                  <div className="ant-form-inline">{renderSizesFields(newItem.sizeGroup?.sizes, newItem.stocks)}</div>
                </Col>
                <Col span={4}>
                  {addingRow && (
                    <Space>
                      <Button htmlType="button" onClick={resetNewRow}>
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        loading={newRowLoading}
                        onClick={() => {
                          newRowForm.submit();
                        }}
                      >
                        Save
                      </Button>
                    </Space>
                  )}
                </Col>
              </Row>
            </Form>
            <Divider />
            <Row>
              <Col flex={1}>
                <Title level={5}>Summary</Title>
              </Col>
              <Col flex={2}>
                <Row justify="end">
                  <Col span={4}>
                    <Title level={5}>Quantity</Title>
                    {resource.quantity}
                  </Col>
                  <Col span={4}>
                    <Title level={5}>Total</Title>
                    {resource.total}
                  </Col>
                  <Col span={4}>
                    <Title level={5}>Taxes</Title>
                    {resource.taxes}
                  </Col>
                  <Col span={4}>
                    <Title level={5}>Grand Total</Title>
                    {resource.grandTotal}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
